import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful login */
  onSuccess?: () => void;
  /** Pre-select tab */
  defaultTab?: "login" | "register";
}

const inp: React.CSSProperties = {
  width: "100%", padding: "11px 13px",
  background: "#111", border: "1px solid #2a2a2a",
  borderRadius: 9, color: "#e0e0e0",
  fontSize: 15, boxSizing: "border-box",
  fontFamily: "inherit", outline: "none",
};

export default function LoginModal({ open, onClose, onSuccess, defaultTab = "login" }: Props) {
  const { login } = useAuth();
  const [tab, setTab]           = useState<"login" | "register" | "forgot">(defaultTab);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [email, setEmail]       = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError]       = useState("");
  const [info, setInfo]         = useState("");
  const [busy, setBusy]         = useState(false);
  const firstInput              = useRef<HTMLInputElement>(null);

  // Password reset state
  const [resetStep, setResetStep]         = useState<1 | 2>(1);
  const [resetUsername, setResetUsername] = useState("");
  const [resetCode, setResetCode]         = useState("");
  const [resetNewPass, setResetNewPass]   = useState("");
  const [resetConfirm, setResetConfirm]   = useState("");

  useEffect(() => {
    if (open) {
      setTab(defaultTab);
      setUsername(""); setPassword(""); setConfirm(""); setEmail("");
      setError(""); setInfo(""); setBusy(false);
      setResetStep(1); setResetUsername(""); setResetCode("");
      setResetNewPass(""); setResetConfirm("");
      setTimeout(() => firstInput.current?.focus(), 80);
    }
  }, [open, defaultTab]);

  if (!open) return null;

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setBusy(true);
    const err = await login(username.trim(), password, rememberMe);
    setBusy(false);
    if (err) { setError(err); return; }
    onSuccess?.();
    onClose();
  }

  async function handleResetRequest(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setBusy(true);
    const r = await fetch("/api/auth/reset-password", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: resetUsername.trim() }),
    });
    const data = await r.json().catch(() => ({ error: "Network error" })) as { ok?: boolean; error?: string };
    setBusy(false);
    if (!r.ok) { setError(data.error ?? "Request failed."); return; }
    setResetStep(2);
    setInfo("If that username has an email on file, a 6-digit code was sent. Check your inbox.");
  }

  async function handleResetConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (resetNewPass !== resetConfirm) { setError("Passwords do not match."); return; }
    if (resetNewPass.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true);
    const r = await fetch("/api/auth/reset-password/confirm", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: resetUsername.trim(), token: resetCode.trim(), newPassword: resetNewPass }),
    });
    const data = await r.json().catch(() => ({ error: "Network error" })) as { ok?: boolean; error?: string };
    setBusy(false);
    if (!r.ok) { setError(data.error ?? "Reset failed."); return; }
    setTab("login");
    setInfo("Password reset! You can now sign in with your new password.");
    setResetStep(1); setResetUsername(""); setResetCode(""); setResetNewPass(""); setResetConfirm("");
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8)  { setError("Password must be at least 8 characters."); return; }
    if (!email.trim())        { setError("An email address is required to register."); return; }
    setBusy(true);
    const r = await fetch("/api/auth/register", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: username.trim(), password, email: email.trim() }),
    });
    const data = await r.json().catch(() => ({ error: "Network error" })) as { error?: string; username?: string };
    setBusy(false);
    if (!r.ok) { setError(data.error ?? "Registration failed."); return; }
    onSuccess?.();
    onClose();
  }

  const goForgot = () => {
    setError(""); setInfo("");
    setResetUsername(username); // pre-fill if they already typed their username
    setTab("forgot");
  };

  const tabBtn = (t: "login" | "register", label: string) => (
    <button
      onClick={() => { setTab(t); setError(""); setInfo(""); }}
      style={{
        flex: 1, padding: "10px 0",
        background: tab === t ? "#1a1a1a" : "transparent",
        border: "none", borderBottom: `2px solid ${tab === t ? "#e8c070" : "transparent"}`,
        color: tab === t ? "#e8c070" : "#555",
        fontWeight: tab === t ? 700 : 400,
        fontSize: 14, cursor: "pointer", fontFamily: "inherit",
        transition: "all 0.15s",
      }}
    >{label}</button>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9500,
        background: "rgba(0,0,0,0.78)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 380,
          background: "#0f0f0f", border: "1px solid #222",
          borderRadius: 18, overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.85)",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        {/* Header */}
        <div style={{ padding: "20px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>
              {tab === "login" ? "Sign In" : tab === "register" ? "Create Account" : "Reset Password"}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {tab === "login" ? "Access TubeDealer" : tab === "register" ? "Start your free 5-day trial" : "We'll email you a reset code"}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: "#555", fontSize: 22, cursor: "pointer", lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        {/* Tabs — hidden on the forgot-password view */}
        {tab !== "forgot" && (
          <div style={{ display: "flex", margin: "16px 24px 0", borderBottom: "1px solid #1a1a1a" }}>
            {tabBtn("login", "Sign In")}
            {tabBtn("register", "Create Account")}
          </div>
        )}

        {/* Body */}
        <div style={{ padding: "20px 24px 24px" }}>
          {error && (
            <div style={{ background: "#1e0808", border: "1px solid #4a1a1a", borderRadius: 9, padding: "10px 13px", marginBottom: 16, color: "#ff8080", fontSize: 13, lineHeight: 1.5 }}>
              {error}
            </div>
          )}
          {info && (
            <div style={{ background: "#0d1a0d", border: "1px solid #1f4d1f", borderRadius: 9, padding: "10px 13px", marginBottom: 16, color: "#5bbd72", fontSize: 13, lineHeight: 1.5 }}>
              {info}
            </div>
          )}

          {tab === "login" ? (
            <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 13 }}>
              <div>
                <label htmlFor="lm-login-username" style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>Username</label>
                <input
                  id="lm-login-username"
                  ref={firstInput}
                  value={username} onChange={e => setUsername(e.target.value)}
                  placeholder="your_username" autoComplete="username" required style={inp}
                />
              </div>
              <div>
                <label htmlFor="lm-login-password" style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>Password</label>
                <input
                  id="lm-login-password"
                  type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" autoComplete="current-password" required style={inp}
                />
              </div>
              {/* Stay logged in + forgot password */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", userSelect: "none" }}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: "#e8c070", cursor: "pointer" }}
                  />
                  <span style={{ fontSize: 13, color: "#888" }}>Stay logged in for 30 days</span>
                </label>
                <button type="button" onClick={goForgot}
                  style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: 0, textDecoration: "underline" }}>
                  Forgot password?
                </button>
              </div>

              <button type="submit" disabled={busy || !username.trim() || !password} style={{
                padding: "13px 0",
                background: busy || !username.trim() || !password ? "#1a1a1a" : "#e8c070",
                color: busy || !username.trim() || !password ? "#444" : "#0a0a0a",
                border: "none", borderRadius: 10,
                fontWeight: 700, fontSize: 15,
                cursor: busy || !username.trim() || !password ? "not-allowed" : "pointer",
                fontFamily: "inherit", transition: "background 0.15s, color 0.15s",
              }}>
                {busy ? "Signing in…" : "Sign In"}
              </button>
              <div style={{ textAlign: "center", fontSize: 12, color: "#444", marginTop: 4 }}>
                Don't have an account?{" "}
                <button type="button" onClick={() => { setTab("register"); setError(""); }}
                  style={{ background: "none", border: "none", color: "#e8c070", cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: 0 }}>
                  Create one free
                </button>
              </div>
            </form>
          ) : tab === "forgot" ? (
            <>
              {resetStep === 1 ? (
                <form onSubmit={handleResetRequest} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ fontSize: 13, color: "#777", lineHeight: 1.6 }}>
                    Enter your username and we'll email you a 6-digit code to reset your password. The code expires in 15 minutes.
                  </div>
                  <div>
                    <label htmlFor="lm-reset-username" style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>Username</label>
                    <input
                      id="lm-reset-username"
                      value={resetUsername} onChange={e => setResetUsername(e.target.value)}
                      placeholder="your_username" autoComplete="username" required style={inp}
                    />
                  </div>
                  <button type="submit" disabled={busy || !resetUsername.trim()} style={{
                    padding: "13px 0",
                    background: busy || !resetUsername.trim() ? "#1a1a1a" : "#e8c070",
                    color: busy || !resetUsername.trim() ? "#444" : "#0a0a0a",
                    border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15,
                    cursor: busy || !resetUsername.trim() ? "not-allowed" : "pointer",
                    fontFamily: "inherit", transition: "background 0.15s, color 0.15s",
                  }}>
                    {busy ? "Sending…" : "Send Reset Code"}
                  </button>
                  <div style={{ textAlign: "center" }}>
                    <button type="button" onClick={() => { setTab("login"); setError(""); setInfo(""); }}
                      style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: 0 }}>
                      ← Back to Sign In
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleResetConfirm} style={{ display: "flex", flexDirection: "column", gap: 13 }}>
                  <div>
                    <label htmlFor="lm-reset-code" style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>6-Digit Code</label>
                    <input
                      id="lm-reset-code"
                      value={resetCode} onChange={e => setResetCode(e.target.value)}
                      placeholder="123456" maxLength={6} required style={inp}
                      inputMode="numeric" autoComplete="one-time-code"
                    />
                  </div>
                  <div>
                    <label htmlFor="lm-reset-newpass" style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>New Password</label>
                    <input
                      id="lm-reset-newpass"
                      type="password" value={resetNewPass} onChange={e => setResetNewPass(e.target.value)}
                      placeholder="at least 8 characters" autoComplete="new-password" required style={inp}
                    />
                  </div>
                  <div>
                    <label htmlFor="lm-reset-confirm" style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>Confirm New Password</label>
                    <input
                      id="lm-reset-confirm"
                      type="password" value={resetConfirm} onChange={e => setResetConfirm(e.target.value)}
                      placeholder="••••••••" autoComplete="new-password" required style={inp}
                    />
                  </div>
                  <button type="submit" disabled={busy || !resetCode.trim() || !resetNewPass || !resetConfirm} style={{
                    padding: "13px 0",
                    background: busy || !resetCode.trim() || !resetNewPass || !resetConfirm ? "#1a1a1a" : "#e8c070",
                    color: busy || !resetCode.trim() || !resetNewPass || !resetConfirm ? "#444" : "#0a0a0a",
                    border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15,
                    cursor: busy || !resetCode.trim() || !resetNewPass || !resetConfirm ? "not-allowed" : "pointer",
                    fontFamily: "inherit", transition: "background 0.15s, color 0.15s",
                  }}>
                    {busy ? "Resetting…" : "Reset Password"}
                  </button>
                  <div style={{ textAlign: "center", display: "flex", justifyContent: "center", gap: 16 }}>
                    <button type="button" onClick={() => { setResetStep(1); setError(""); setInfo(""); }}
                      style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: 0 }}>
                      ← Resend code
                    </button>
                    <button type="button" onClick={() => { setTab("login"); setError(""); setInfo(""); }}
                      style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: 0 }}>
                      Back to Sign In
                    </button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <form onSubmit={handleRegister} style={{ display: "flex", flexDirection: "column", gap: 13 }}>
              <div>
                <label htmlFor="lm-reg-username" style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>Username</label>
                <input
                  id="lm-reg-username"
                  ref={firstInput}
                  value={username} onChange={e => setUsername(e.target.value)}
                  placeholder="pick_a_username" autoComplete="username" required style={inp}
                />
              </div>
              <div>
                <label htmlFor="lm-reg-email" style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>Email</label>
                <input
                  id="lm-reg-email"
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" autoComplete="email" required style={inp}
                />
              </div>
              <div>
                <label htmlFor="lm-reg-password" style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>Password</label>
                <input
                  id="lm-reg-password"
                  type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="at least 8 characters" autoComplete="new-password" required style={inp}
                />
              </div>
              <div>
                <label htmlFor="lm-reg-confirm" style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>Confirm Password</label>
                <input
                  id="lm-reg-confirm"
                  type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  placeholder="••••••••" autoComplete="new-password" required style={inp}
                />
              </div>
              <div style={{ background: "#0d1a0d", border: "1px solid #1a3a1a", borderRadius: 9, padding: "10px 13px", fontSize: 12, color: "#5bbd72", lineHeight: 1.55 }}>
                ✓ Free 5-day trial — no credit card needed
              </div>
              <button type="submit" disabled={busy || !username.trim() || !email.trim() || !password || !confirm} style={{
                marginTop: 2, padding: "13px 0",
                background: busy || !username.trim() || !email.trim() || !password || !confirm ? "#1a1a1a" : "#e8c070",
                color: busy || !username.trim() || !email.trim() || !password || !confirm ? "#444" : "#0a0a0a",
                border: "none", borderRadius: 10,
                fontWeight: 700, fontSize: 15,
                cursor: busy || !username.trim() || !email.trim() || !password || !confirm ? "not-allowed" : "pointer",
                fontFamily: "inherit", transition: "background 0.15s, color 0.15s",
              }}>
                {busy ? "Creating account…" : "Start Free Trial"}
              </button>
              <div style={{ textAlign: "center", fontSize: 12, color: "#444", marginTop: 2 }}>
                Already have an account?{" "}
                <button type="button" onClick={() => { setTab("login"); setError(""); }}
                  style={{ background: "none", border: "none", color: "#e8c070", cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: 0 }}>
                  Sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
